/**
 * Flow context — carries selections across the multi-step registration
 * flow without forcing every route to thread props.
 *
 * Stateful pieces:
 *   - selectedDivision   set on EventPage when coach taps a division
 *   - selectedTeam       set on TeamPicker when coach picks a team
 *   - registration       set after POST /teams/{id}/register succeeds
 *   - completedRegs      list of completed registrations across the
 *                        "register another team?" loop, drives the
 *                        SummaryScreen rendering
 *
 * NOT in here: the embed-scoped JWT (lives in sessionStorage so it
 * survives page reloads and the back button) and the EmbedClient API
 * client (passed by props from App.tsx so it's testable).
 */
import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

import type {
  TeamSummary, TeamRegistration,
  SponsorshipTier, SponsorCheckoutBody, SponsorshipConfirmResponse,
  AvailableEvent, BatchRegisterResponse, BatchRegistration,
} from '@/api/embed';

interface FlowState {
  selectedDivision: string | null;
  selectedTeam: TeamSummary | null;
  registration: TeamRegistration | null;
  completedRegs: TeamRegistration[];
  /**
   * Whether the coach left the "Create a TourneyNation account"
   * checkbox checked on the Confirm page. Read on the DonePage after
   * payment confirms to decide whether to call promote-to-account.
   * Default true — the checkbox starts checked.
   */
  createAccount: boolean;

  // ── sponsorship sub-flow ──────────────────────────────────────────
  // Held here (not component-local) because App.tsx remounts the inner
  // page subtree on every navigation (key={location.pathname}), which
  // would otherwise wipe selections between the tier → details →
  // payment → done hops.
  selectedSponsorship: SponsorshipTier | null;
  sponsorForm: SponsorCheckoutBody | null;
  sponsorResult: SponsorshipConfirmResponse | null;

  /** Charity add-ons chosen on the Confirm step, keyed by product id →
   *  quantity. Read on the Payment step and folded into the single
   *  registration PaymentIntent. Cleared by resetForNewRegistration. */
  addonQuantities: Record<string, number>;

  // ── bulk sub-flow (one team, several events, one payment) ─────────
  /** Events picked on /bulk/events, in the order shown. */
  bulkEvents: AvailableEvent[];
  /** Division per picked event, keyed by event id. */
  bulkDivisions: Record<string, string>;
  /** The created batch (set on Review → Continue to payment). */
  bulkBatch: BatchRegisterResponse | null;
  /** Registrations as confirmed after payment, for the Done screen. */
  bulkConfirmed: BatchRegistration[];
  /** Event carried over from a single registration's Confirm upsell. */
  bulkCarriedId: string | null;
  /** Registration-question answers per picked event, keyed by question id. */
  bulkAnswers: Record<string, Record<string, string>>;
}

interface FlowContextValue extends FlowState {
  selectDivision: (division: string) => void;
  selectTeam: (team: TeamSummary) => void;
  setRegistration: (r: TeamRegistration | null) => void;
  appendCompletedReg: (r: TeamRegistration) => void;
  resetForNewRegistration: () => void;
  setCreateAccount: (v: boolean) => void;
  selectSponsorship: (s: SponsorshipTier | null) => void;
  setSponsorForm: (f: SponsorCheckoutBody | null) => void;
  setSponsorResult: (r: SponsorshipConfirmResponse | null) => void;
  setAddonQuantity: (productId: string, qty: number) => void;
  setBulkEvents: (events: AvailableEvent[]) => void;
  setBulkDivision: (eventId: string, division: string) => void;
  setBulkBatch: (batch: BatchRegisterResponse | null) => void;
  setBulkConfirmed: (regs: BatchRegistration[]) => void;
  resetBulk: () => void;
  setBulkAnswers: (eventId: string, answers: Record<string, string>) => void;
  /** Seed the bulk cart with one event (its division and any answers) from Confirm. */
  startBulkFrom: (
    event: AvailableEvent,
    division: string | null,
    answers?: Record<string, string>,
  ) => void;
}

const FlowContext = createContext<FlowContextValue | null>(null);

/** When the iframe is loaded with `?studio=1` (theme-studio mode),
 *  seed FlowContext with realistic mock state so the studio's page
 *  nav can jump directly to Confirm / Payment / Done without the
 *  coach walking the whole flow each time tokens are tweaked.
 *  Inert outside studio mode — production reads through `null`s. */
function studioSeed(): FlowState | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('studio') !== '1') return null;
  const team: TeamSummary = {
    id: 'studio-team-mock',
    name: 'Hartford Thunder U10',
    sport: 'Baseball',
    age_group: 'U10',
    gender: 'Boys',
    city: 'Hartford',
    state: 'CT',
    roster_count: 12,
  };
  // event_id here matches the studio server's mock — the same string
  // appears in PAGE_NAV URLs (`evt-spring-classic`).
  const registration: TeamRegistration = {
    id: 'studio-reg-mock',
    team_id: team.id,
    event_id: 'evt-spring-classic',
    status: 'confirmed',
    division: 'U10 Boys AAA',
    amount_cents: 14500,
    created_at: new Date().toISOString(),
  };
  return {
    selectedDivision: 'U10 Boys AAA',
    selectedTeam: team,
    registration,
    completedRegs: [registration],
    createAccount: true,
    selectedSponsorship: null,
    sponsorForm: null,
    sponsorResult: null,
    addonQuantities: {},
    ...EMPTY_BULK,
  };
}

const EMPTY_BULK = {
  bulkEvents: [] as AvailableEvent[],
  bulkDivisions: {} as Record<string, string>,
  bulkBatch: null as BatchRegisterResponse | null,
  bulkConfirmed: [] as BatchRegistration[],
  bulkCarriedId: null as string | null,
  bulkAnswers: {} as Record<string, Record<string, string>>,
};

export function FlowProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FlowState>(() => studioSeed() ?? {
    selectedDivision: null,
    selectedTeam: null,
    registration: null,
    completedRegs: [],
    createAccount: true,
    selectedSponsorship: null,
    sponsorForm: null,
    sponsorResult: null,
    addonQuantities: {},
    ...EMPTY_BULK,
  });

  const selectDivision = useCallback((division: string) => {
    setState((s) => ({ ...s, selectedDivision: division }));
  }, []);

  const selectTeam = useCallback((team: TeamSummary) => {
    setState((s) => ({ ...s, selectedTeam: team }));
  }, []);

  const setRegistration = useCallback((r: TeamRegistration | null) => {
    setState((s) => ({ ...s, registration: r }));
  }, []);

  const appendCompletedReg = useCallback((r: TeamRegistration) => {
    setState((s) => ({
      ...s,
      completedRegs: [...s.completedRegs, r],
    }));
  }, []);

  /** Clear per-team selections after the coach taps "Register another
   *  team" — keep the completedRegs list so the SummaryScreen can
   *  show every registration completed in this session. */
  const resetForNewRegistration = useCallback(() => {
    setState((s) => ({
      ...s,
      selectedDivision: null,
      selectedTeam: null,
      registration: null,
      addonQuantities: {},
    }));
  }, []);

  const setCreateAccount = useCallback((v: boolean) => {
    setState((s) => ({ ...s, createAccount: v }));
  }, []);

  const selectSponsorship = useCallback((sp: SponsorshipTier | null) => {
    setState((s) => ({ ...s, selectedSponsorship: sp }));
  }, []);

  const setSponsorForm = useCallback((f: SponsorCheckoutBody | null) => {
    setState((s) => ({ ...s, sponsorForm: f }));
  }, []);

  const setSponsorResult = useCallback((r: SponsorshipConfirmResponse | null) => {
    setState((s) => ({ ...s, sponsorResult: r }));
  }, []);

  const setAddonQuantity = useCallback((productId: string, qty: number) => {
    setState((s) => {
      const next = { ...s.addonQuantities };
      if (qty > 0) next[productId] = qty;
      else delete next[productId];
      return { ...s, addonQuantities: next };
    });
  }, []);

  const setBulkEvents = useCallback((events: AvailableEvent[]) => {
    setState((s) => {
      // Keep divisions only for events still picked.
      const keep = new Set(events.map((e) => e.id));
      const bulkDivisions = Object.fromEntries(
        Object.entries(s.bulkDivisions).filter(([id]) => keep.has(id)),
      );
      const bulkAnswers = Object.fromEntries(
        Object.entries(s.bulkAnswers).filter(([id]) => keep.has(id)),
      );
      return { ...s, bulkEvents: events, bulkDivisions, bulkAnswers, bulkBatch: null };
    });
  }, []);

  const setBulkDivision = useCallback((eventId: string, division: string) => {
    setState((s) => ({
      ...s,
      bulkDivisions: { ...s.bulkDivisions, [eventId]: division },
      bulkBatch: null,
    }));
  }, []);

  const setBulkBatch = useCallback((batch: BatchRegisterResponse | null) => {
    setState((s) => ({ ...s, bulkBatch: batch }));
  }, []);

  const setBulkConfirmed = useCallback((regs: BatchRegistration[]) => {
    setState((s) => ({ ...s, bulkConfirmed: regs }));
  }, []);

  const resetBulk = useCallback(() => {
    setState((s) => ({ ...s, ...EMPTY_BULK, selectedTeam: null }));
  }, []);

  const setBulkAnswers = useCallback((eventId: string, answers: Record<string, string>) => {
    setState((s) => ({
      ...s,
      bulkAnswers: { ...s.bulkAnswers, [eventId]: answers },
      bulkBatch: null,
    }));
  }, []);

  const startBulkFrom = useCallback((
    event: AvailableEvent,
    division: string | null,
    answers?: Record<string, string>,
  ) => {
    setState((s) => ({
      ...s,
      ...EMPTY_BULK,
      bulkEvents: [event],
      bulkDivisions: division ? { [event.id]: division } : {},
      bulkAnswers: answers && Object.keys(answers).length ? { [event.id]: answers } : {},
      bulkCarriedId: event.id,
    }));
  }, []);

  const value: FlowContextValue = {
    ...state,
    selectDivision,
    selectTeam,
    setRegistration,
    appendCompletedReg,
    resetForNewRegistration,
    setCreateAccount,
    selectSponsorship,
    setSponsorForm,
    setSponsorResult,
    setAddonQuantity,
    setBulkEvents,
    setBulkDivision,
    setBulkBatch,
    setBulkConfirmed,
    resetBulk,
    setBulkAnswers,
    startBulkFrom,
  };

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}

export function useFlow(): FlowContextValue {
  const ctx = useContext(FlowContext);
  if (!ctx) {
    throw new Error('useFlow must be called inside <FlowProvider>');
  }
  return ctx;
}
